import React, { useState } from 'react'
import classNames from 'classnames'
import { HistoryEntry, HistoryEvent } from '../types'
import { formatDateTime } from '../utils'

type Props = {
  entries: HistoryEntry[]
  loading?: boolean
}

const EVENT_TONE: Record<HistoryEvent, string> = {
  RECEIVED: 'brand',
  UPLOADED: 'brand',
  EXTRACTED: 'brand',
  VALIDATED: 'success',
  CORRECTED: 'brand',
  SAVED: 'success',
  NEEDS_REVIEW: 'warning',
  FAILED: 'danger'
}

/**
 * Processing milestones in plain language; the raw processing log is one
 * click away for support.
 */
const ActivityTimeline: React.FC<Props> = ({ entries, loading }) => {
  const [showTechnical, setShowTechnical] = useState(false)

  if (loading) {
    return (
      <div className="dx-skeleton" aria-busy>
        <span style={{ width: '70%' }} />
        <span style={{ width: '55%' }} />
        <span style={{ width: '62%' }} />
      </div>
    )
  }

  const visible = showTechnical ? entries : entries.filter((e) => e.event)
  const hiddenCount = entries.length - entries.filter((e) => e.event).length

  if (!visible.length) {
    return <p className="dx-muted">No activity recorded yet.</p>
  }

  return (
    <>
      <ol className="dx-timeline">
        {visible.map((entry, i) => (
          <li
            // Log ids repeat as null for the synthetic "received" entry.
            // eslint-disable-next-line react/no-array-index-key
            key={`${entry.id ?? 'r'}-${i}`}
            className={classNames(
              'dx-timeline__item',
              entry.event
                ? `dx-timeline__item--${EVENT_TONE[entry.event]}`
                : 'dx-timeline__item--technical'
            )}
          >
            <span className="dx-timeline__dot" aria-hidden />
            <div className="dx-timeline__body">
              <span className="dx-timeline__msg" title={entry.detail}>
                {entry.message}
              </span>
              <span className="dx-timeline__time">
                {formatDateTime(entry.at)}
              </span>
              {showTechnical &&
                entry.detail &&
                entry.detail !== entry.message && (
                  <code className="dx-timeline__detail">{entry.detail}</code>
                )}
            </div>
          </li>
        ))}
      </ol>
      {hiddenCount > 0 && (
        <button
          type="button"
          className="dx-link-btn"
          onClick={() => setShowTechnical((v) => !v)}
        >
          {showTechnical
            ? 'Hide technical log'
            : `Show technical log (${hiddenCount} more)`}
        </button>
      )}
    </>
  )
}

export default ActivityTimeline
